import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getTridImportantDates, updateTridImportantDates } from 'services'
import { openS3Document } from 'services/apis/documents'
import { svgLoading } from 'stories/assets'
import { borrowerFullName } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  AdditionalPreApprovalIssued,
  defaultInputs,
  PRE_APPROVAL_HMDA_NOT_REQUESTED,
  RESPA6_COLLECTED,
  ROWS,
} from './constants'
import { canShowRespa6Date } from './logic'

export default function TridImportantDates() {
  const borrower = useSelector((state: any) => state.borrower.borrower)

  const app1Name = borrower ? borrowerFullName(borrower) : ''

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs())
  const [preApprovalIssuedFileKey, setPreApprovalIssuedFileKey] = useState('')
  const [additionalPreApprovals, setAdditionalPreApprovals] = useState<AdditionalPreApprovalIssued[]>([])

  const setValues = (data: Record<string, any>) => {
    const newInputs = cloneDeep(defaultInputs())
    Object.keys(newInputs).forEach((key) => {
      if (data?.[key] !== undefined) newInputs[key].value = data[key]
    })
    if (!newInputs.preApprovalHmdaSelection.value) {
      newInputs.preApprovalHmdaSelection.value = PRE_APPROVAL_HMDA_NOT_REQUESTED
    }
    setInputs(newInputs)
    setPreApprovalIssuedFileKey(data?.preApprovalIssuedFileKey || '')
    setAdditionalPreApprovals(Array.isArray(data?.additionalPreApprovalIssued) ? data.additionalPreApprovalIssued : [])
  }

  useEffect(() => {
    setLoading(true)
    getTridImportantDates()
      .then((res: any) => {
        if (res?.success) setValues(res.data || {})
      })
      .finally(() => setLoading(false))
  }, [])

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onBlur = async (key: string) => {
    setSaving(true)
    try {
      const res = await updateTridImportantDates({ [key]: inputs[key].value })
      if (res?.success && res.data) {
        setValues(res.data)
        window.dispatchEvent(new CustomEvent('trid-important-dates-updated'))
      }
    } finally {
      setSaving(false)
    }
  }

  const onChangeLocked = async (key: string, value: any) => {
    onChange(key, value)
    setSaving(true)
    try {
      const res: any = await updateTridImportantDates({ [key]: value })
      if (res?.success && res.data) {
        setValues(res.data)
        window.dispatchEvent(new CustomEvent('trid-important-dates-updated'))
      }
    } finally {
      setSaving(false)
    }
  }

  const onChangeHmdaSelection = async (key: string, value: any) => {
    onChange(key, value)
    setSaving(true)
    try {
      const res: any = await updateTridImportantDates({ [key]: value })
      if (res?.success && res.data) {
        setValues(res.data)
        window.dispatchEvent(new CustomEvent('trid-important-dates-updated'))
      }
    } finally {
      setSaving(false)
    }
  }

  const renderDownload = (fileKey?: string) => {
    if (!fileKey) return null
    return (
      <span
        className="text-shade-blue p-1 hover-shadow1 cursor-pointer inline-flex shrink-0 mb-1"
        onClick={() => openS3Document(fileKey, true)}
        title="Download Pre-Approval"
      >
        <ArrowDownTrayIcon className="w-4 h-4" />
      </span>
    )
  }

  const showRespa6Row = canShowRespa6Date(inputs)

  return (
    <div className="relative">
      <LayoutLoading show={loading} />

      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
        Pre-Approval
        {saving && <img src={svgLoading} className="inline w-5 h-5 animate-spin" />}
      </h2>

      <div className="w-full max-w-xl mb-10">
        <div className="mb-4">
          <RenderInput
            input={inputs.preApprovalHmdaSelection}
            Key="preApprovalHmdaSelection"
            onChange={onChangeHmdaSelection}
          />
        </div>

        <div className="mb-4 flex gap-2 items-end">
          <div className="flex-1">
            <RenderInput
              input={inputs.preApprovalIssuedDate}
              Key="preApprovalIssuedDate"
              onChange={onChange}
              onBlur={onBlur}
            />
          </div>
          {renderDownload(preApprovalIssuedFileKey)}
        </div>

        {additionalPreApprovals.map((item, index) => (
          <div key={`additional-preapproval-${index}`} className="mb-4 flex gap-2 items-end">
            <div className="flex-1">
              <RenderInput
                input={{
                  inputType: 'text',
                  type: 'date',
                  title: 'Additional Pre-Approval Issued Date',
                  value: item.date || '',
                  readOnly: true,
                }}
                Key={`additionalPreApprovalIssued_${index}`}
                onChange={() => {}}
              />
            </div>
            {renderDownload(item.fileKey)}
          </div>
        ))}
      </div>

      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">TRID Important Dates</h2>

      <div className="w-full">
        {ROWS.filter((row) => row.collected !== RESPA6_COLLECTED || showRespa6Row).map((row) => (
          <div key={row.collected} className="mb-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <RenderInput
                  input={{
                    ...inputs[row.collected],
                    readOnly: row.collected === RESPA6_COLLECTED || !!inputs[row.locked].value,
                  }}
                  Key={row.collected}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              </div>

              <div className="flex justify-center pb-2 w-16 shrink-0">
                <RenderInput input={inputs[row.locked]} Key={row.locked} onChange={onChangeLocked} />
              </div>

              <div className="flex-1">
                {row.entered ? (
                  <RenderInput
                    input={{ ...inputs[row.entered], readOnly: !!inputs[row.locked].value }}
                    Key={row.entered}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ) : (
                  <div />
                )}
              </div>
            </div>

            {row.app1Date && (
              <div className="mt-2 flex gap-4 items-end">
                <div className="flex-1">
                  <RenderInput
                    input={{ inputType: 'text', title: 'App 1', value: app1Name, readOnly: true }}
                    Key={`app1_${row.app1Date}`}
                    onChange={() => {}}
                  />
                </div>
                <div className="w-16 shrink-0" />
                <div className="flex-1">
                  <RenderInput input={inputs[row.app1Date]} Key={row.app1Date} onChange={onChange} onBlur={onBlur} />
                </div>
              </div>
            )}
          </div>
        ))}

        <div className="mt-8 max-w-xl border-t pt-6">
          <RenderInput
            input={inputs.loanEstimateDisclosedDate}
            Key="loanEstimateDisclosedDate"
            onChange={onChange}
            onBlur={onBlur}
          />
        </div>
      </div>
    </div>
  )
}
